import React, { FC } from 'react';
import { useParams } from 'react-router';
import { useBannersQuery } from '../../useBannersQuery';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import { LoadingWrapper } from '../../../shared/components/LoadingWrapper';
import { BannerSlider } from '../../../shared/slider/BannerSlider';
import { BannerItem } from '../../../shared/slider/BannerItem';
import compact from 'lodash/compact';
import { useCategoryPageQuery } from '../useCategoryPageQuery';
import RoutingUtils from '../../../shared/utils/utils-routing';
import { GenrePageSidebar } from './GenrePageSidebar';
import { ContainerInner } from '../../../shared/common/ContainerInner';
import { BlankCoverProvider } from '../../../contexts/blank-cover';
import { Link } from 'react-router-dom';
import Utils from '../../../shared/utils';
import { CatalogueProductGroup } from '../../../shared/product/ProductGroup';
import find from 'lodash/find';
import './GenreEntryPage.scss';
import BreadCrumbNav from '../../../shared/navigation/BreadcrumbNav';
import { FetchCategoryPageResponse } from '../../../api/entryPage/fetchCategoryPage';
import { cn } from '@bem-react/classname';
import { LoanFormat, parseLoanFormat } from '../../../../models/LoanFormat';
import { ProductSpec } from '../../../store/api';
import { useTranslation } from 'react-i18next';
import { getGenreId, getSubGenreId } from '../../../shared/common/translationMappers/genreIdMapper';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../../shared/utils/togglz';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';

const block = cn('genre-entry-page');

export const GenreEntryPage: FC = () => {
    const { t } = useTranslation();
    const {
        genre,
        subGenre,
        loanFormat: loanFormatParam,
        shopLanguage
    } = useParams<{ genre?: string; subGenre?: string; loanFormat: string; shopLanguage?: string }>();
    const loanFormat = parseLoanFormat(loanFormatParam) ?? LoanFormat.eBooks;
    const { value: ageCategory, searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    const categoryKey = decodeURIComponent(compact([genre, subGenre]).join('->'));
    const banners = useBannersQuery('GENRES', { loanFormat, categoryKey, ageCategory, shopLanguage });
    const categoryPage = useCategoryPageQuery('genres', { loanFormat, ageCategory, shopLanguage });
    const isLoading = categoryPage.status === 'loading';
    const genresPath = isMultiLanguageShopEnabled ? `shop/${shopLanguage}/${loanFormat}` : `catalogue/${loanFormat}`;

    const breadcrumbs = {
        key: 'genres',
        label: 'Genres',
        buckets: categoryPage.data.buckets,
        activeBucket: categoryKey
    };

    return (
        <div className={block()}>
            {banners.data.length > 0 && (
                <BannerSlider
                    itemWidth={600}
                    itemHeight={200}
                    showButtons={true}
                    showDots={false}
                    showShadows={false}
                    minimumItemsToShow={3}
                    buttonPosition="bottom"
                >
                    {banners.data.map((bannerItem, i) => (
                        <BannerItem key={i} banner={bannerItem} />
                    ))}
                </BannerSlider>
            )}
            <div className={block('breadcrumbs')}>
                <BreadCrumbNav
                    breadcrumbs={breadcrumbs}
                    fixedLabel={t('components.genreSidebar.genres')}
                    path={genresPath}
                    customGenreBase="genres"
                    floatingClass=""
                />
            </div>
            <ContainerInner className="entry-page" isLoading={isLoading}>
                {isLoading ? (
                    <LoadingWrapper>
                        <LoadingOverlay loading={true} />
                    </LoadingWrapper>
                ) : (
                    <>
                        <div className={block('sidebar')}>
                            <GenrePageSidebar
                                key="genre-page-sidebar"
                                topGenres={[...categoryPage.data.topBuckets]}
                                genres={[...categoryPage.data.buckets]}
                                selectedGenre={categoryKey}
                                loanFormatUrlKey={loanFormat.toLowerCase()}
                                itemTargetBuilder={(loanFormat, parentId, childId) =>
                                    RoutingUtils.genrePageLocationDescriptor({
                                        loanFormat,
                                        genreId: parentId,
                                        subGenreId: childId,
                                        shopLanguage,
                                        isMultiLanguageShopEnabled,
                                        ageCategory: ageCategorySearchParam
                                    })
                                }
                            />
                        </div>
                        <div className={block('inner-content min-h-screen')}>
                            <BlankCoverProvider value={false}>
                                <GenreProductGroups
                                    categoryKey={categoryKey}
                                    categoryPage={categoryPage.data}
                                    loanFormat={loanFormat}
                                    shopLanguage={shopLanguage}
                                />
                            </BlankCoverProvider>
                            {categoryKey && (
                                <SeeMoreProducts
                                    genre={categoryKey}
                                    loanFormat={loanFormat}
                                    shopLanguage={shopLanguage}
                                />
                            )}
                        </div>
                    </>
                )}
            </ContainerInner>
        </div>
    );
};

type SeeMoreProductsProps = {
    readonly genre: string;
    readonly loanFormat: LoanFormat;
    readonly shopLanguage: string | undefined;
};
const SeeMoreProducts: FC<SeeMoreProductsProps> = ({ genre, loanFormat, shopLanguage }) => {
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    return (
        <div className="see-complete-list">
            <Link
                to={Utils.catalogueLocationDescriptorString({
                    loanFormat: loanFormat.toUpperCase(),
                    filter: {
                        genre: { include: [genre] }
                    },
                    shopLanguage,
                    isMultiLanguageShopEnabled,
                    ageCategory: ageCategorySearchParam
                })}
                className="see-all"
            >
                {t('seeCompleteProductList')}
                <i className="material-icons">keyboard_arrow_right</i>
            </Link>
        </div>
    );
};

type GenreProductGroupsProps = {
    categoryKey: string;
    categoryPage: FetchCategoryPageResponse;
    readonly loanFormat: LoanFormat;
    readonly shopLanguage: string | undefined;
};

const GenreProductGroups = React.memo<GenreProductGroupsProps>(
    ({ categoryKey, categoryPage, loanFormat, shopLanguage }) => {
        const { t } = useTranslation();
        const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
        const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

        if (categoryPage.buckets.length === 0) {
            return null;
        }

        if (!categoryKey) {
            return (
                <>
                    {categoryPage.topBuckets.map(bucket => {
                        const title = t(`filters:genre.${getGenreId(bucket.key)}`, {
                            defaultValue: bucket.value
                        });
                        const filters = {
                            genre: { include: [bucket.key] }
                        };
                        const productSpec: ProductSpec = {
                            filters,
                            loanFormat: loanFormat
                        };
                        const seeAllUrl = Utils.catalogueLocationDescriptorString({
                            loanFormat,
                            filter: filters,
                            shopLanguage,
                            isMultiLanguageShopEnabled,
                            ageCategory: ageCategorySearchParam
                        });
                        return (
                            <CatalogueProductGroup
                                key={bucket.value}
                                title={title}
                                productSpec={productSpec}
                                seeAllUrl={seeAllUrl}
                                shopLanguage={shopLanguage}
                            />
                        );
                    })}
                </>
            );
        } else {
            const releaseFilter = {
                genre: { include: [categoryKey] },
                releaseDate: { include: ['1M-3M', '*-1M'] }
            };
            const bestsellerFilter = {
                genre: { include: [categoryKey] },
                bestseller: { exclude: ['$NO_VALUE$'] }
            };
            const releaseProductSpec: ProductSpec = {
                filters: releaseFilter,
                loanFormat
            };
            const bestsellerProductSpec: ProductSpec = {
                filters: bestsellerFilter,
                loanFormat
            };
            const subGenreList = find(categoryPage.buckets, { key: categoryKey })?.buckets ?? [];

            return (
                <>
                    <CatalogueProductGroup
                        key={'New Releases'}
                        title={t('shop.catalogue.productGroup.newReleases')}
                        productSpec={releaseProductSpec}
                        seeAllUrl={Utils.catalogueLocationDescriptorString({
                            loanFormat,
                            filter: releaseFilter,
                            shopLanguage,
                            isMultiLanguageShopEnabled,
                            ageCategory: ageCategorySearchParam
                        })}
                        shopLanguage={shopLanguage}
                    />
                    <CatalogueProductGroup
                        key={'Bestsellers'}
                        title={t('shop.catalogue.productGroup.bestSellers')}
                        productSpec={bestsellerProductSpec}
                        seeAllUrl={Utils.catalogueLocationDescriptorString({
                            loanFormat,
                            filter: bestsellerFilter,
                            shopLanguage,
                            isMultiLanguageShopEnabled,
                            ageCategory: ageCategorySearchParam
                        })}
                        shopLanguage={shopLanguage}
                    />
                    {subGenreList.map(subGenre => {
                        const filters = {
                            genre: { include: [subGenre.key] }
                        };
                        const subGenreId = getSubGenreId(subGenre.key);
                        const productSpec: ProductSpec = {
                            filters,
                            loanFormat
                        };
                        const seeAllUrl = Utils.catalogueLocationDescriptorString({
                            loanFormat,
                            filter: filters,
                            shopLanguage,
                            isMultiLanguageShopEnabled,
                            ageCategory: ageCategorySearchParam
                        });
                        return (
                            <CatalogueProductGroup
                                key={subGenre.value}
                                title={t(`filters:genre.${subGenreId}`, {
                                    defaultValue: `${subGenre.value}`
                                })}
                                productSpec={productSpec}
                                seeAllUrl={seeAllUrl}
                                shopLanguage={shopLanguage}
                            />
                        );
                    })}
                </>
            );
        }
    }
);
